import { useQuery } from "react-query";
import { loggedInApiCall } from "../../axiosClient/userApi";
import { zodParseWithLog } from "../../apiErrorLogs";
import { TagValue, TagContext } from "../../../types/tag";
import { z } from "zod";
interface ApiTagTypePartial {
    id: string;
    name: string;
    context?: TagContext;
    color?: string;
    multiple?: boolean;
    required?: boolean;
}
interface ApiTagValueInList {
    id: string;
    name: string;
    slug: string;
    description?: string;
    color?: string;
    sortOrder: number;
    metadata?: Record<string, unknown>;
    createdAt: string;
    updatedAt: string;
    tagType: ApiTagTypePartial;
}
interface ApiTagValuesListResponse {
    "hydra:member": ApiTagValueInList[];
    "hydra:totalItems": number;
    "hydra:view"?: {
        "@id": string;
        "hydra:first"?: string;
        "hydra:last"?: string;
        "hydra:previous"?: string;
        "hydra:next"?: string;
    };
}

const ApiTagTypePartialSchema: z.ZodSchema<ApiTagTypePartial> = z.object({
    id: z.string(),
    name: z.string(),
    context: z.enum(["global", "product", "ingredient", "cms", "supplier", "organization", "packaging"]).optional(),
    color: z.string().optional(),
    multiple: z.boolean().optional(),
    required: z.boolean().optional(),
});

const ApiTagValueInListSchema: z.ZodSchema<ApiTagValueInList> = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    description: z.string().optional(),
    color: z.string().optional(),
    sortOrder: z.number(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    tagType: ApiTagTypePartialSchema,
});

const ApiTagValuesListResponseSchema: z.ZodSchema<ApiTagValuesListResponse> = z.object({
    "hydra:member": z.array(ApiTagValueInListSchema),
    "hydra:totalItems": z.number(),
    "hydra:view": z
        .object({
            "@id": z.string(),
            "hydra:first": z.string().optional(),
            "hydra:last": z.string().optional(),
            "hydra:previous": z.string().optional(),
            "hydra:next": z.string().optional(),
        })
        .optional(),
});

const convertApiTagValueToTagValue = (apiTagValue: ApiTagValueInList): TagValue => {
    return {
        id: apiTagValue.id,
        name: apiTagValue.name,
        slug: apiTagValue.slug,
        description: apiTagValue.description,
        color: apiTagValue.color,
        sortOrder: apiTagValue.sortOrder,
        metadata: apiTagValue.metadata,
        createdAt: apiTagValue.createdAt,
        updatedAt: apiTagValue.updatedAt,
        tagType: {
            id: apiTagValue.tagType.id,
            name: apiTagValue.tagType.name,
            slug: "",
            description: "",
            context: apiTagValue.tagType.context || "global",
            color: apiTagValue.tagType.color || "#ccc",
            active: true,
            required: apiTagValue.tagType.required ?? false,
            multiple: apiTagValue.tagType.multiple ?? true,
            sortOrder: 0,
            metadata: {},
            createdAt: "",
            updatedAt: "",
            company: undefined,
        },
    };
};
export interface TagValueListParams {
    page?: number;
    itemsPerPage?: number;
    tagTypeId?: string;
    active?: boolean;
}

export const useGetAllTagValues = (params: TagValueListParams = {}) => {
    const { page = 1, itemsPerPage = 30, tagTypeId, active } = params;

    return useQuery({
        queryKey: ["tagValues", { page, itemsPerPage, tagTypeId, active }],
        queryFn: async (): Promise<{ tagValues: TagValue[]; totalItems: number }> => {
            const searchParams = new URLSearchParams({
                page: page.toString(),
                itemsPerPage: itemsPerPage.toString(),
            });

            if (tagTypeId) {
                searchParams.append("tagType", tagTypeId);
            }
            if (active !== undefined) {
                searchParams.append("active", active.toString());
            }

            const response = await loggedInApiCall.get(`/api/tag_values?${searchParams.toString()}`);
            const validatedResponse = zodParseWithLog(response.data, ApiTagValuesListResponseSchema, "getAllTagValues");
            const tagValues = validatedResponse["hydra:member"].map(convertApiTagValueToTagValue);

            return {
                tagValues,
                totalItems: validatedResponse["hydra:totalItems"],
            };
        },
        cacheTime: 300000,
    });
};
